import React from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useTenant } from '../../lib/tenant-context';
import { useLoyaltyDashboard, useLoyaltyToggle } from '../../lib/loyalty-hooks';
import { Card } from '../../components/ui/Card';
import { Users, Star, Gift, Trophy, Power, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
export function LoyaltyDashboardPage() {
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const {
    customers,
    loading
  } = useLoyaltyDashboard(restaurant.restaurant_id);
  const {
    isActive,
    loading: toggleLoading,
    saving,
    toggle
  } = useLoyaltyToggle(restaurant.restaurant_id);
  const totalCustomers = customers.length;
  const totalPoints = customers.reduce((sum, c) => sum + (c.current_points || 0), 0);
  const totalRewardsAvailable = customers.reduce((sum, c) => sum + (c.available_rewards_count || 0), 0);
  const totalRewardsRedeemed = customers.reduce((sum, c) => sum + (c.redeemed_rewards_count || 0), 0);
  return <AdminLayout restaurant={restaurant} role={role} onSignOut={signOut} data-id="element-3700">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-3701">
        <div data-id="element-3702">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-3703">
            Programa de Lealtad
          </h1>
          <p className="text-gray-500" data-id="element-3704">
            Gestiona tus clientes frecuentes y beneficios
          </p>
        </div>

        {/* Activation toggle */}
        <Card className={`p-6 border-2 transition-colors ${isActive ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-gray-50'}`} data-id="element-3705">
          <div className="flex items-center justify-between gap-6 flex-wrap" data-id="element-3706">
            <div className="flex items-center gap-4" data-id="element-3707">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isActive ? 'bg-emerald-100' : 'bg-gray-200'}`} data-id="element-3708">
                <Power className={`w-6 h-6 ${isActive ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-3709" />
              </div>
              <div data-id="element-3710">
                <div className="flex items-center gap-2" data-id="element-3711">
                  <h2 className="font-bold text-gray-900" data-id="element-3712">
                    Sistema de Lealtad
                  </h2>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`} data-id="element-3713">
                    {toggleLoading ? '...' : isActive ? 'ACTIVO' : 'INACTIVO'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5" data-id="element-3714">
                  {isActive ? 'Cada pedido pagado con teléfono acumula puntos para el cliente.' : 'Los pedidos no acumulan puntos. Activa el programa para empezar a premiar a tus clientes.'}
                </p>
              </div>
            </div>

            <button onClick={() => toggle(!isActive)} disabled={saving || toggleLoading} className={`relative inline-flex items-center h-9 px-1 rounded-full transition-colors w-20 shrink-0 disabled:opacity-60 ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} aria-label={isActive ? 'Desactivar lealtad' : 'Activar lealtad'} data-id="element-3715">
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-white shadow transform transition-transform ${isActive ? 'translate-x-11' : 'translate-x-0'}`} data-id="element-3716">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" data-id="element-3717" />}
              </span>
            </button>
          </div>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3718">
          <Card className="p-6" data-id="element-3719">
            <div className="flex items-center gap-4" data-id="element-3720">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3721">
                <Users className="w-6 h-6 text-blue-600" data-id="element-3722" />
              </div>
              <div data-id="element-3723">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3724">
                  Clientes Leales
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3725">
                  {totalCustomers}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-3726">
            <div className="flex items-center gap-4" data-id="element-3727">
              <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3728">
                <Star className="w-6 h-6 text-yellow-600" data-id="element-3729" />
              </div>
              <div data-id="element-3730">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3731">
                  Puntos Emitidos
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3732">
                  {totalPoints}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-3733">
            <div className="flex items-center gap-4" data-id="element-3734">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3735">
                <Gift className="w-6 h-6 text-green-600" data-id="element-3736" />
              </div>
              <div data-id="element-3737">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3738">
                  Beneficios Disponibles
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3739">
                  {totalRewardsAvailable}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-3740">
            <div className="flex items-center gap-4" data-id="element-3741">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3742">
                <Trophy className="w-6 h-6 text-purple-600" data-id="element-3743" />
              </div>
              <div data-id="element-3744">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3745">
                  Beneficios Redimidos
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3746">
                  {totalRewardsRedeemed}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Customer Table */}
        <Card className="overflow-hidden" data-id="element-3747">
          <div className="p-6 border-b border-gray-200" data-id="element-3748">
            <h2 className="font-bold text-gray-900" data-id="element-3749">Directorio de Clientes</h2>
          </div>
          <div className="overflow-x-auto" data-id="element-3750">
            <table className="w-full text-left text-sm" data-id="element-3751">
              <thead className="bg-gray-50 text-gray-500" data-id="element-3752">
                <tr data-id="element-3753">
                  <th className="px-6 py-4 font-medium" data-id="element-3754">Cliente</th>
                  <th className="px-6 py-4 font-medium" data-id="element-3755">Teléfono</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3756">Visitas</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3757">
                    Total Gastado
                  </th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3758">
                    Ticket Promedio
                  </th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3759">Puntos</th>
                  <th className="px-6 py-4 font-medium" data-id="element-3760">Nivel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-3761">
                {loading ? <tr data-id="element-3762">
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500" data-id="element-3763">
                      Cargando clientes...
                    </td>
                  </tr> : customers.length === 0 ? <tr data-id="element-3764">
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500" data-id="element-3765">
                      No hay clientes registrados aún.
                    </td>
                  </tr> : customers.map(customer => <tr key={customer.customer_id} className="hover:bg-gray-50" data-id="element-3766">
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3767">
                        {customer.name || 'Sin nombre'}
                      </td>
                      <td className="px-6 py-4 text-gray-500" data-id="element-3768">
                        {customer.phone}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900" data-id="element-3769">
                        {customer.total_orders}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900" data-id="element-3770">
                        {formatCurrency(customer.total_spent, restaurant.currency_symbol)}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-500" data-id="element-3771">
                        {formatCurrency(customer.total_spent / Math.max(1, customer.total_orders), restaurant.currency_symbol)}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-brand-charcoal" data-id="element-3772">
                        {customer.current_points}
                      </td>
                      <td className="px-6 py-4" data-id="element-3773">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-3774">
                          {customer.current_level || 'Inicial'}
                        </span>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminLayout>;
}